/**
 * Shared deal slot pricing helpers (DealSummary, SideCart, cartSlice).
 * BackOffice may send legacy types (NoDiscount, Fixed) with customPrice for "£X each" deals.
 */

export function parseSlotCustomPrice(slotCustomPrice) {
  if (slotCustomPrice == null || slotCustomPrice === "") return null;
  const n = parseFloat(slotCustomPrice);
  return !isNaN(n) && n >= 0 ? n : null;
}

export function normalizeSlotPricingType(pricingType) {
  return String(pricingType ?? "").trim();
}

export function isFixedPricePerSlotType(pricingType) {
  return normalizeSlotPricingType(pricingType) === "FixedPricePerSlot";
}

/** True when each selected item in the slot should use customPrice (e.g. £5 each). */
export function isFixedPricePerItemType(pricingType, slotCustomPrice = null) {
  const pt = normalizeSlotPricingType(pricingType);
  const cp = parseSlotCustomPrice(slotCustomPrice);
  if (cp == null) return false;
  if (pt === "CustomPrice" || pt === "FixedPricePerItem") return true;
  if (isFixedPricePerSlotType(pt)) return false;
  if (!pt || pt === "NoDiscount" || pt === "Fixed") return cp > 0;
  return false;
}

export function getPerItemCustomPrice(pricingType, slotCustomPrice) {
  return isFixedPricePerItemType(pricingType, slotCustomPrice)
    ? parseSlotCustomPrice(slotCustomPrice)
    : null;
}

export function slotHasExplicitPricing(slot) {
  if (!slot) return false;
  const pt = normalizeSlotPricingType(slot.pricingType || slot.PricingType);
  const isFree = slot.isFree || slot.IsFree || false;
  const pct = slot.discountPercentage ?? slot.DiscountPercentage;
  const customPrice = slot.customPrice ?? slot.CustomPrice;
  if (pt === "Free" || isFree || pt === "HalfPrice") return true;
  if ((pt === "Discount" || pt === "CustomDiscount") && pct != null && Number(pct) > 0) return true;
  if (isFixedPricePerSlotType(pt) && parseSlotCustomPrice(customPrice) != null) return true;
  if (isFixedPricePerItemType(pt, customPrice)) return true;
  return false;
}

export function dealHasSlotLevelPricing(dealInfo, dealSlots = null) {
  if (dealInfo?.slots?.some(slotHasExplicitPricing)) return true;
  if (dealSlots?.some(slotHasExplicitPricing)) return true;
  return false;
}

/** Merge cart slot fields with deal definition slot (by slotId). */
export function resolveDealSlotPricing(slot, dealInfo = null) {
  const slotId = slot?.slotId;
  let defSlot = null;
  if (dealInfo?.slots && slotId != null) {
    defSlot = dealInfo.slots.find((s) => String(s.slotId) === String(slotId));
  }
  const pricingType = normalizeSlotPricingType(
    slot?.pricingType || slot?.PricingType || defSlot?.pricingType || defSlot?.PricingType
  );
  const customPrice = slot?.customPrice ?? slot?.CustomPrice ?? defSlot?.customPrice ?? defSlot?.CustomPrice;
  return {
    pricingType,
    customPrice,
    isFree: !!(slot?.isFree || slot?.IsFree || defSlot?.isFree || defSlot?.IsFree),
    discountPercentage:
      slot?.discountPercentage ??
      slot?.DiscountPercentage ??
      defSlot?.discountPercentage ??
      defSlot?.DiscountPercentage,
    buyQuantity: slot?.buyQuantity ?? slot?.BuyQuantity ?? defSlot?.buyQuantity ?? defSlot?.BuyQuantity,
    autoDuplicateItem:
      slot?.autoDuplicateItem ??
      slot?.AutoDuplicateItem ??
      defSlot?.autoDuplicateItem ??
      defSlot?.AutoDuplicateItem,
  };
}

/** True when this slot is BOGO/Free (buy one get one), not £X each. */
export function isSlotFreePricing(slot, dealInfo = null) {
  const resolved = resolveDealSlotPricing(slot || {}, dealInfo);
  return normalizeSlotPricingType(resolved.pricingType) === "Free" || resolved.isFree === true;
}

/**
 * Auto-duplicate (click one, insert a second copy) is ONLY for Free/BOGO slots
 * that have autoDuplicateItem enabled. "Burger £5 Each" must never clone picks.
 */
export function shouldAutoDuplicateSlot(slot, dealInfo = null) {
  if (!isSlotFreePricing(slot, dealInfo)) return false;
  const resolved = resolveDealSlotPricing(slot || {}, dealInfo);
  return (
    resolved.autoDuplicateItem === true ||
    dealInfo?.autoDuplicateItem === true ||
    dealInfo?.AutoDuplicateItem === true
  );
}

/** Slot picks with bogus auto-duplicated clones removed for paid / £X-each slots. */
export function getRealDealSlotSelectedItems(slot, dealInfo = null) {
  const items = Array.isArray(slot?.selectedItems)
    ? slot.selectedItems
    : Array.isArray(slot?.SelectedItems)
      ? slot.SelectedItems
      : [];
  if (shouldAutoDuplicateSlot(slot, dealInfo)) return items;
  return items.filter((si) => si?.isAutoDuplicated !== true && si?.isAutoDuplicated !== "true");
}

/**
 * "Items at full price" for a slot. With auto-duplicate BOGO the slot holds one chosen
 * item plus a generated copy, so 0 means buy-one-get-one rather than "everything free".
 */
export function getEffectiveBuyQuantity(buyQuantity, autoDuplicateItem) {
  const qty = Number(buyQuantity) || 0;
  return autoDuplicateItem === true && qty === 0 ? 1 : qty;
}

/**
 * True when a Free slot must still charge for this item.
 *
 * Auto-duplicate BOGO keeps the originally chosen item paid and only zeroes the copy it
 * generated. Every caller that prices a Free slot has to agree on this, otherwise the
 * basket, the checkout line and the saved order each show a different amount.
 */
export function chargesFreeSlotItem({ autoDuplicateItem, buyQuantity, isAutoDuplicated }) {
  if (autoDuplicateItem !== true) return false;
  if (getEffectiveBuyQuantity(buyQuantity, autoDuplicateItem) <= 0) return false;
  return isAutoDuplicated !== true;
}

/**
 * After BOGO / half-price branches, apply per-item custom price when legacy type omitted FixedPricePerItem.
 */
export function applyPerItemCustomPriceIfNeeded(finalItemPrice, menuItemPrice, pricingType, slotCustomPrice) {
  const perItem = getPerItemCustomPrice(pricingType, slotCustomPrice);
  if (perItem == null || isFixedPricePerSlotType(pricingType)) return finalItemPrice;
  const menu = parseFloat(menuItemPrice) || 0;
  if (finalItemPrice === menu || finalItemPrice == null) return perItem;
  return finalItemPrice;
}

/**
 * Build display pricing context for the variant/modifier popup from deal + optional slot.
 * Prefers slot-level pricing when the deal uses SlotPricing or the slot has explicit rules.
 */
export function buildDealPopupPricingContext(deal = null, slot = null) {
  const resolved = slot ? resolveDealSlotPricing(slot, deal) : null;
  const dealPt = normalizeSlotPricingType(deal?.dealPricingType || deal?.DealPricingType || deal?.pricingType);
  const slotHasPricing = resolved ? slotHasExplicitPricing({
    pricingType: resolved.pricingType,
    isFree: resolved.isFree,
    discountPercentage: resolved.discountPercentage,
    customPrice: resolved.customPrice,
  }) : false;
  const useSlot =
    !!resolved &&
    (slotHasPricing ||
      dealPt.toLowerCase() === "slotpricing" ||
      !dealPt ||
      dealPt.toLowerCase() === "fixed");

  if (useSlot && resolved) {
    return {
      pricingType: resolved.pricingType,
      discountPercentage: Number(resolved.discountPercentage) || 0,
      customPrice: resolved.customPrice,
      isFree: !!resolved.isFree,
      buyQuantity: Number(resolved.buyQuantity) || 0,
      /** When buy X / rest discounted, don't preview discount on every size before picks are known */
      deferBuyXPreview:
        Number(resolved.buyQuantity) > 0 &&
        (resolved.pricingType === "HalfPrice" ||
          ((resolved.pricingType === "Discount" || resolved.pricingType === "CustomDiscount") &&
            Number(resolved.discountPercentage) > 0)),
    };
  }

  return {
    pricingType: dealPt,
    discountPercentage: Number(deal?.dealDiscountPercentage ?? deal?.DealDiscountPercentage ?? deal?.discountPercentage) || 0,
    customPrice: deal?.customPrice ?? deal?.CustomPrice ?? null,
    isFree: false,
    buyQuantity: Number(deal?.buyQuantity ?? deal?.BuyQuantity) || 0,
    deferBuyXPreview: false,
  };
}

/**
 * Map a catalog amount to what the guest should see under the active deal/slot rule.
 * @returns {{ original: number, effective: number, showStrike: boolean }}
 */
export function getDealScenarioDisplayPrice(originalPrice, pricingContext = null) {
  const original = parseFloat(originalPrice) || 0;
  if (!pricingContext || original <= 0) {
    return { original, effective: original, showStrike: false };
  }

  if (pricingContext.deferBuyXPreview) {
    return { original, effective: original, showStrike: false };
  }

  const pt = normalizeSlotPricingType(pricingContext.pricingType);
  const ptLower = pt.toLowerCase();
  const pct = Number(pricingContext.discountPercentage) || 0;
  const custom = parseSlotCustomPrice(pricingContext.customPrice);

  if (pt === "Free" || pricingContext.isFree) {
    return { original, effective: 0, showStrike: original > 0 };
  }

  if (isFixedPricePerItemType(pt, pricingContext.customPrice) && custom != null) {
    return {
      original,
      effective: custom,
      showStrike: original > 0 && Math.abs(original - custom) > 0.001,
    };
  }

  if (pt === "HalfPrice" || ptLower === "halfprice") {
    const half = original * 0.5;
    return { original, effective: half, showStrike: true };
  }

  if (
    (pt === "Discount" ||
      pt === "CustomDiscount" ||
      ptLower === "discount" ||
      ptLower === "customdiscount") &&
    pct > 0
  ) {
    const discounted = original - (original * pct) / 100;
    return { original, effective: discounted, showStrike: true };
  }

  return { original, effective: original, showStrike: false };
}
